import type { PoolIntervalItem } from "@/domains/validators/graphql-service/pools"

export const SECONDS_PER_DAY = 86_400

/**
 * Unix timestamp for the start of the current UTC calendar day.
 */
export function utcDayStartUnix(nowSec: number = Math.floor(Date.now() / 1_000)): number {
  return Math.floor(nowSec / SECONDS_PER_DAY) * SECONDS_PER_DAY
}

type PoolIntervalField = "volumeUSD" | "feesUSD"

function getUtcDayMetricUsd(
  poolDayData: ReadonlyArray<PoolIntervalItem>,
  poolHourData: ReadonlyArray<PoolIntervalItem>,
  field: PoolIntervalField,
  nowSec: number = Math.floor(Date.now() / 1_000),
): number {
  const today = poolDayData[0]
  const todayValue = today?.[field]
  if (typeof todayValue === "number" && Number.isFinite(todayValue)) {
    return todayValue
  }

  const dayStart = utcDayStartUnix(nowSec)
  let total = 0
  let sawHourlyInWindow = false

  for (const row of poolHourData) {
    const start = row.periodStartUnix
    if (start === undefined || start < dayStart) continue
    sawHourlyInWindow = true
    const value = row[field]
    if (typeof value === "number" && Number.isFinite(value)) {
      total += value
    }
  }

  if (sawHourlyInWindow) {
    return total
  }

  return 0
}

/**
 * UTC calendar-day (today) pool volume in USD. Uses `poolDayData[0]` when the
 * indexer has materialised today's daily bucket; otherwise sums hourly rows
 * since UTC midnight for indexer lag. Never falls back to yesterday's day row.
 */
export function getUtcDayVolumeUsd(
  poolDayData: ReadonlyArray<PoolIntervalItem>,
  poolHourData: ReadonlyArray<PoolIntervalItem>,
  nowSec?: number,
): number {
  return getUtcDayMetricUsd(poolDayData, poolHourData, "volumeUSD", nowSec)
}

/**
 * UTC calendar-day (today) pool fees in USD. Same resolution order as
 * {@link getUtcDayVolumeUsd}.
 */
export function getUtcDayFeesUsd(
  poolDayData: ReadonlyArray<PoolIntervalItem>,
  poolHourData: ReadonlyArray<PoolIntervalItem>,
  nowSec?: number,
): number {
  return getUtcDayMetricUsd(poolDayData, poolHourData, "feesUSD", nowSec)
}
